import darkThemeCss from '@actual-app/components/themes/dark.css?inline';
import lightThemeCss from '@actual-app/components/themes/light.css?inline';
import midnightThemeCss from '@actual-app/components/themes/midnight.css?inline';
import paletteCss from '@actual-app/components/themes/palette.css?inline';
import presupuestoDarkCss from '@actual-app/components/themes/presupuesto-dark.css?inline';
import presupuestoLightCss from '@actual-app/components/themes/presupuesto-light.css?inline';
import sidebarRedesignLightCss from '@actual-app/components/themes/sidebar-redesign-light.css?inline';
import type { Theme } from '@actual-app/core/types/prefs';

import { useGlobalPref } from '#hooks/useGlobalPref';

// Temas propios del fork (concepto A · Cartera): capa encima de light/dark.
// Son los temas por defecto: «auto» elige entre ellos según el sistema.
const themes = {
  'presupuesto-dark': {
    name: 'Rumbo oscuro',
    colors: `${darkThemeCss}\n${presupuestoDarkCss}`,
  },
  'presupuesto-light': {
    name: 'Rumbo claro',
    colors: `${lightThemeCss}\n${presupuestoLightCss}`,
  },
  light: { name: 'Light', colors: lightThemeCss },
  dark: { name: 'Dark', colors: darkThemeCss },
  midnight: { name: 'Midnight', colors: midnightThemeCss },
  auto: { name: 'System default' },
} as const;

/** Tema claro que usa «auto» cuando el sistema está en modo claro. */
const AUTO_LIGHT_THEME = 'presupuesto-light';

/** ¿El tema elegido es oscuro? (para «auto» decide el sistema). */
export function isDarkThemeName(theme: string | undefined): boolean {
  if (theme === 'auto' || theme === undefined) {
    return !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  }
  return (
    theme === 'dark' || theme === 'midnight' || theme === 'presupuesto-dark'
  );
}

type Traductor = (clave: string) => string;

// Rumbo: el selector solo ofrece sus temas y el automático. Los de Actual
// («Light», «Dark», «Midnight») siguen existiendo para leer preferencias
// antiguas (se ven como los de Rumbo, ver `temaPropio`), pero no se ofrecen.
// Los nombres se traducen aquí (las claves de `themes` no pasan por t()).
export function getThemeOptions(t: Traductor): Array<[Theme, string]> {
  return [
    ['auto', t('Automatic (like your phone)')],
    ['presupuesto-light', t('Rumbo light')],
    ['presupuesto-dark', t('Rumbo dark')],
  ];
}

// Presupuesto: las preferencias guardadas con los temas de Actual («dark»,
// «midnight», «light») se leen como los temas propios, para que un archivo
// importado antes del pase visual no se quede con el morado de Actual.
function temaPropio<T extends string>(theme: T): T {
  if (theme === 'dark' || theme === 'midnight') {
    return 'presupuesto-dark' as T;
  }
  if (theme === 'light') {
    return 'presupuesto-light' as T;
  }
  return theme;
}

export function useTheme() {
  const [theme = 'auto', setThemePref] = useGlobalPref('theme');
  return [temaPropio(theme), setThemePref] as const;
}

export function usePreferredDarkTheme() {
  const [darkTheme = 'presupuesto-dark', setDarkTheme] =
    useGlobalPref('preferredDarkTheme');
  return [temaPropio(darkTheme), setDarkTheme] as const;
}

function getBaseThemeColors(baseTheme: string | undefined) {
  // Theme prefs are untrusted strings; unknown values resolve to undefined.
  return baseTheme !== undefined &&
    baseTheme !== 'auto' &&
    Object.hasOwn(themes, baseTheme)
    ? themes[baseTheme as Exclude<keyof typeof themes, 'auto'>].colors
    : undefined;
}

export function ThemeStyle() {
  const [activeTheme] = useTheme();
  const [darkThemePreference] = usePreferredDarkTheme();

  // Rendered rather than injected from an effect so the CSS variables exist
  // in the same commit as any consumer effect that reads them.
  const themeColors = getBaseThemeColors(
    activeTheme === 'auto' ? AUTO_LIGHT_THEME : activeTheme,
  );

  if (!themeColors) return null;

  // Los temas claros usan siempre la paleta nueva de la barra lateral.
  const sidebarRedesignCss =
    themeColors === themes.light.colors ||
    themeColors === themes['presupuesto-light'].colors
      ? sidebarRedesignLightCss
      : null;

  if (activeTheme !== 'auto') {
    return (
      <>
        <style>{paletteCss}</style>
        <style>{themeColors}</style>
        {sidebarRedesignCss != null && <style>{sidebarRedesignCss}</style>}
      </>
    );
  }

  // Let the browser pick the sheet so system theme changes need no re-render.
  const darkColors =
    getBaseThemeColors(darkThemePreference) ??
    themes['presupuesto-dark'].colors;

  return (
    <>
      <style>{paletteCss}</style>
      <style media="(prefers-color-scheme: light)">{themeColors}</style>
      {sidebarRedesignCss != null && (
        <style media="(prefers-color-scheme: light)">
          {sidebarRedesignCss}
        </style>
      )}
      <style media="(prefers-color-scheme: dark)">{darkColors}</style>
    </>
  );
}
